// Tiny localStorage adapter for data-seam surfaces that have no sphinx REST
// endpoint yet and don't warrant one (per-suite host config, project server
// config, server attachments). Keyed under `sphinx:`; `useLocalValue`
// re-renders subscribers when `writeLocal` runs in this tab.
import { useSyncExternalStore } from "react";

const PREFIX = "sphinx:";
const listeners = new Set<() => void>();
const memory = new Map<string, unknown>();

function storage(): Storage | null {
  try {
    return typeof window !== "undefined" ? window.localStorage : null;
  } catch {
    return null;
  }
}

export function readLocal<T>(key: string, fallback: T): T {
  if (memory.has(key)) return memory.get(key) as T;
  const raw = storage()?.getItem(PREFIX + key) ?? null;
  if (raw === null) return fallback;
  try {
    const parsed = JSON.parse(raw) as T;
    memory.set(key, parsed);
    return parsed;
  } catch {
    return fallback;
  }
}

export function writeLocal<T>(key: string, value: T): void {
  memory.set(key, value);
  try {
    storage()?.setItem(PREFIX + key, JSON.stringify(value));
  } catch {
    // quota / private mode: in-memory copy still serves this tab
  }
  for (const l of listeners) l();
}

export function removeLocal(key: string): void {
  memory.delete(key);
  try {
    storage()?.removeItem(PREFIX + key);
  } catch {
    // ignore
  }
  for (const l of listeners) l();
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Reactive read; `key === null` → `undefined` (mirrors a skipped query). */
export function useLocalValue<T>(key: string | null, fallback: T): T | undefined {
  return useSyncExternalStore(
    subscribe,
    () => (key === null ? undefined : readLocal(key, fallback)),
    () => (key === null ? undefined : fallback)
  );
}
